import React, { useState, useEffect, forwardRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ButtonBase, ButtonBaseProps, PortalProps, useTheme } from '@mui/material';
import { getFocusRingStyle } from '@/src/components/focusring/focusring';

export interface BypassBlockProps extends ButtonBaseProps<'a'> {
  /**
   * Button label, e.g. 'Skip to content'
   */
  children: ReactNode;
  /**
   * ID of content section, e.g. '#content'
   * @default '#'
   */
  href: string;
  /**
   * Prevent push of new link into history stack
   * @default false
   */
  replace?: boolean;
  /**
   * Programmatically control if button should be visible
   * @default false
   */
  active?: boolean;
  /**
   * props applied to portal moving the button at the top
   */
  portalProps?: PortalProps;
}

/**
 * Accessibility button to quickly scroll into the content area.
 *  - first focusable element on the page
 *  - only visible when focused
 */
export const BypassBlock: React.FC<BypassBlockProps> = forwardRef(
  ({ children, className, href = '#', sx, replace = false, active = false, onClick, portalProps, ...rest }, ref) => {
    const theme = useTheme();

    return (
      <PrependPortal disablePortal={true} {...portalProps}>
        <ButtonBase
          tabIndex={0}
          component="a"
          href={href}
          onClick={(e) => {
            onClick?.(e);
            if (replace) {
              // prevent push of # into url
              e.preventDefault();
              // use js to scroll into element
              document?.getElementById(href?.replace('#', ''))?.scrollIntoView(true);
            }
          }}
          className={active ? [`Mui-focusVisible`, className].filter(Boolean).join(' ') : className}
          sx={{
            position: 'absolute',
            zIndex: `${theme.zIndex.tooltip + 100}`,
            top: '10px',
            // hidden by default, still have to be at least 1px for the Safari focus
            width: '1px',
            height: '1px',
            margin: '-1px',
            clip: 'rect(0 0 0 0)',
            overflow: 'hidden',
            padding: '0',
            border: 'none',
            whiteSpace: 'nowrap',
            // style
            backgroundColor: 'onSurface.os900',
            color: 'onSurface.os0',
            borderRadius: '4px',
            ...theme.typography.body,
            // focus
            '&.Mui-focusVisible': {
              ...getFocusRingStyle(theme, false),
              padding: '8px 24px',
              width: 'auto',
              height: 'auto',
              overflow: 'visible',
              clip: 'unset',
              margin: '0',
              left: '10px',
            },
            ...sx,
          }}
          ref={ref}
          {...rest}
        >
          {children}
        </ButtonBase>
      </PrependPortal>
    );
  },
);

/**
 * Portal component that create extra div prepended instead of appended to the container.
 * This way element can be placed at the begging of container instead of at the end.
 */
function PrependPortal({ children, container, disablePortal }: PortalProps) {
  const [portalContainer] = useState(() => document?.createElement('div'));

  useEffect(() => {
    // obtain proper container for placement (default body tag)
    const body = (typeof container === 'function' ? container() : container) || document?.body;

    // prepend (add into begging) extra div into body
    body && portalContainer && !disablePortal && body.prepend(portalContainer);
    return () => {
      // remove div with unmount
      portalContainer && !disablePortal && portalContainer.remove();
    };
  }, []);

  // utilize createPortal to place children into container at the begging of body
  return portalContainer && !disablePortal ? createPortal(children, portalContainer) : children;
}
